import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatTooltip } from '@angular/material/tooltip';
import { FieldFilterService } from '../../../services/filters/field-filter.service';
import { ExportType } from '../../../models/export-type';

@Component({
  selector: 'app-export-types-select',
  imports: [
    MatFormField, MatLabel, MatError,
    MatSelect, MatSelectTrigger, MatOption,
    ReactiveFormsModule,
    MatTooltip,
  ],
  templateUrl: './export-types-select.component.html',
  styleUrls: ['./export-types-select.component.scss'],
})
export class ExportTypesSelectComponent implements OnInit {
  @Input() form: FormGroup | null = null;
  @Input() controlName = 'export-types';
  @Input() label = 'Export Types';
  @Input() placeholder = 'Make a Selection';
  @Input() appearance = 'outline';
  @Input() floatLabel = 'always';
  @Input() color = 'primary';
  @Input() required = false;
  @Input() toolTipPosition = 'above';
  @Input() selectAllEnabled = true;

  @Output() readyEvent = new EventEmitter<void>();
  @Output() valueChangedEvent = new EventEmitter<ExportType[] | null>();

  @ViewChild(MatSelect) matSelect!: MatSelect;

  exportTypes: ExportType[] = [];
  allOption: ExportType = { id: 0, description: 'All' };
  control = new FormControl<ExportType[]>([]);

  constructor(private filterService: FieldFilterService) {}

  ngOnInit() {
    if (this.required) {
      this.control.setValidators([Validators.required]);
    }

    if (this.form) {
      this.form.addControl(this.controlName, this.control);
    }

    this.filterService.getExportTypeOptions().subscribe(results => {
      this.exportTypes = results.map(r => ({
        id: r.ID ?? 0,
        description: r.Description ?? ''
      }));
      this.control.setValue(this.getSelectAllValue(this.exportTypes));
      this.valueChangedEvent.emit(this.getValues());
      this.readyEvent.emit();
    });
  }

  getValues(): ExportType[] | null {
    if (!this.control.value) {
      return null;
    }

    return this.control.value.filter(option => option.id !== this.allOption.id);
  }

  compareById(a: ExportType | null, b: ExportType | null): boolean {
    if (a == null || b == null) {
      return a === b;
    }

    return a.id === b.id;
  }

  setValues(values: ExportType[]) {
    const nextValues = values ?? [];
    this.control.setValue(this.getSelectAllValue(nextValues));
  }

  onSelectionChange() {
    this.valueChangedEvent.emit(this.getValues());
  }

  getTooltipString(): string {
    return (this.getValues() ?? []).map(o => o.description).join(', ');
  }

  getSelectCount(): number {
    const values = this.control.value ?? [];
    return values.length > 0 && values.some(option => option.id === this.allOption.id)
      ? values.length - 1
      : values.length;
  }

  onToggleItem(item: MatOption) {
    if (!this.selectAllEnabled) {
      return;
    }

    if (item.value === this.allOption) {
      if (item.selected) {
        this.selectAllOptions();
        return;
      }

      this.deselectAllOptions();
      return;
    }

    const allItem = this.matSelect.options.get(0);
    if (!allItem) {
      return;
    }

    if (!item.selected && allItem.selected) {
      allItem.deselect();
      return;
    }

    if (item.selected && !allItem.selected && this.areAllOptionsSelected()) {
      allItem.select();
    }
  }

  private getSelectAllValue(values: ExportType[]): ExportType[] {
    if (!this.selectAllEnabled) {
      return values;
    }

    if (values.length > 0 && values.length === this.exportTypes.length) {
      return [this.allOption, ...values];
    }

    return values;
  }

  private selectAllOptions() {
    this.matSelect.options.forEach(option => option.select());
  }

  private deselectAllOptions() {
    this.matSelect.options.forEach(option => option.deselect());
  }

  private areAllOptionsSelected(): boolean {
    return this.matSelect.options.toArray().every(option => option.value === this.allOption || option.selected);
  }
}
